import type { ReactElement } from 'react';
import { useI18n } from '../lib/i18n/I18nProvider';
import type { Readiness, ReadinessCheck } from '../lib/api/types';
import type { StringKey } from '../lib/i18n/strings';
import { Badge, DataTable, Ltr } from './primitives';

/**
 * PUBLICATION READINESS IS THE SERVER'S ANSWER, RENDERED — NOT THE CLIENT'S GUESS.
 *
 * The guard that actually refuses a publish lives inside `SetPublicationState`, so a screen that
 * re-derived "is this booth ready" from the rows it happens to have read would disagree with the
 * only opinion that matters the moment either side changed. `GET /v1/admin/participations/{id}/readiness`
 * (and the same block embedded in the developer booth read) names every check, whether it is
 * required, and `publishable`; this panel draws exactly that and adds nothing.
 *
 * It is READ-ONLY on both surfaces. The admin console offers the verbs beside it; the exhibitor's
 * copy offers none, because an exhibitor cannot publish their own booth.
 */

const CHECK_KEYS: Record<string, StringKey> = {
  organization_active: 'readiness.check.organization_active',
  template_assigned: 'readiness.check.template_assigned',
  slot_assigned: 'readiness.check.slot_assigned',
  layout_published: 'readiness.check.layout_published',
  branding_logo: 'readiness.check.branding_logo',
  branding_hero_banner: 'readiness.check.branding_hero_banner',
  branding_screen_video: 'readiness.check.branding_screen_video',
  seats_issued: 'readiness.check.seats_issued',
};

type Tone = 'neutral' | 'ok' | 'warn' | 'danger';

/**
 * `ok` is the server's own boolean; `status` refines a branding check that is NOT ok, because
 * "waiting for a reviewer" and "never uploaded" are different things to do something about.
 */
export function readinessTone(check: ReadinessCheck): { tone: Tone; label: StringKey } {
  if (check.ok) return { tone: 'ok', label: 'readiness.state.ok' };
  if (check.status === 'pending') return { tone: 'warn', label: 'readiness.state.pending' };
  if (check.status === 'rejected') return { tone: 'danger', label: 'readiness.state.rejected' };
  return { tone: check.required ? 'danger' : 'neutral', label: 'readiness.state.missing' };
}

export function ReadinessPanel({ readiness }: { readiness: Readiness }): ReactElement {
  const { t } = useI18n();
  const checkLabel = (key: string): ReactElement | string => {
    const stringKey = CHECK_KEYS[key];
    return stringKey ? t(stringKey) : <Ltr>{key}</Ltr>;
  };

  return (
    <div className="readiness" data-testid="readiness-panel">
      <p className="readiness__verdict">
        <Badge tone={readiness.publishable ? 'ok' : 'warn'}>
          {readiness.publishable ? t('readiness.publishable') : t('readiness.blocked')}
        </Badge>
      </p>
      <DataTable<ReadinessCheck>
        caption={t('readiness.title')}
        rows={readiness.checks}
        rowKey={(c) => c.key}
        columns={[
          { key: 'check', header: t('readiness.check'), cell: (c) => checkLabel(c.key) },
          {
            key: 'required',
            header: t('readiness.requirement'),
            cell: (c) => (
              <Badge tone="neutral">{c.required ? t('readiness.required') : t('readiness.optional')}</Badge>
            ),
          },
          {
            key: 'state',
            header: t('table.status'),
            cell: (c) => {
              const { tone, label } = readinessTone(c);
              return <Badge tone={tone}>{t(label)}</Badge>;
            },
          },
          {
            key: 'detail',
            header: t('readiness.detail'),
            cell: (c) =>
              c.status || c.code ? (
                <Ltr>{[c.status, c.code].filter(Boolean).join(' · ')}</Ltr>
              ) : (
                t('table.none')
              ),
          },
        ]}
      />
    </div>
  );
}
